import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_DONE = "done";
const STATUS_FAILED = "failed";

const LOOKUP_IDENTIFIER = "identifier";
const LOOKUP_TITLE = "title";

const MATCH_EXACT = "exact";
const MATCH_PREFIX = "prefix";

const ID_COL_AUTO = "";
const ID_COL_SKU = "sku";
const ID_COL_PRODUCT_ID = "shopify_product_id";
const ID_COL_HANDLE = "handle";
const ID_COL_TITLE = "title";

const ID_COL_OPTIONS = [
  { value: ID_COL_AUTO, label: "Auto (SKU → ID → Handle)" },
  { value: ID_COL_SKU, label: "SKU column" },
  { value: ID_COL_PRODUCT_ID, label: "Shopify Product ID column" },
  { value: ID_COL_HANDLE, label: "Handle column" },
  { value: ID_COL_TITLE, label: "Title column" },
];

const ID_COL_HELPER = {
  [ID_COL_AUTO]:
    "Tries SKU first, then Shopify Product ID, then Handle. Pick a specific column if your file has overlapping identifiers and one is wrong.",
  [ID_COL_SKU]:
    "Uses only the SKU column (accepts headers 'SKU', 'Variant SKU', or 'Parent SKU'). Matches the parent SKU on Shopify.",
  [ID_COL_PRODUCT_ID]:
    "Uses only the Shopify Product ID column (numeric or full GID). Misses won't fall back to other columns.",
  [ID_COL_HANDLE]:
    "Uses only the Handle (URL slug) column. Useful when SKUs in the file don't match Shopify yet.",
  [ID_COL_TITLE]:
    "Looks up by product title (case-insensitive). Use when no SKU/ID/Handle is available — e.g. an inventory export sheet.",
};

const ID_COL_LABEL = ID_COL_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const lookupModeFor = (idCol) =>
  idCol === ID_COL_TITLE ? LOOKUP_TITLE : LOOKUP_IDENTIFIER;

const matchHelperText = (matchMode) =>
  matchMode === MATCH_EXACT
    ? "Reports a match only when a product tag equals the value exactly (case-insensitive)."
    : "Reports every tag on the product whose name starts with the value (case-insensitive). E.g. 'on_map_' matches 'on_map_2026-05-21' and 'on_map_2026-04-15'.";

const MATCH_LABEL = {
  [MATCH_EXACT]: "Exact",
  [MATCH_PREFIX]: "Prefix",
};

const LOOKUP_LABEL = {
  [LOOKUP_IDENTIFIER]: "Identifier",
  [LOOKUP_TITLE]: "Title",
};

const ENDPOINT_FOR_LOOKUP = {
  [LOOKUP_IDENTIFIER]: "/shopify/lookup-product-tag",
  [LOOKUP_TITLE]: "/shopify/lookup-product-tag-by-title",
};

function TagLookupTool() {
  const [inputFile, setInputFile] = useState(null);
  const [tag, setTag] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [identifierColumn, setIdentifierColumn] = useState(ID_COL_AUTO);
  const [matchMode, setMatchMode] = useState(MATCH_EXACT);
  const [includeArchived, setIncludeArchived] = useState(false);
  const lookupMode = lookupModeFor(identifierColumn);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/shopify/lookup-product-tag/jobs",
    cacheKey: "tagLookup.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Tag Lookup",
    description:
      "Check which Shopify products in an uploaded CSV/XLSX currently have a given tag (exact or prefix). Read-only — no products are modified. Runs as a background job.",
    steps: [
      "Upload a CSV or XLSX. Recognized identifier columns: SKU (also 'Variant SKU' / 'Parent SKU'), SHOPIFY_PRODUCT_ID, HANDLE, or 'Product Title' (also accepts 'Title', 'Name', or 'Product Name').",
      "Pick the lookup column — Auto cascades SKU → ID → Handle, or force a specific column. Use 'Title column' for inventory exports that only have product names.",
      "Pick match mode — Exact for a single tag value, Prefix to find any tag starting with the value (e.g. 'offmap_').",
      "Enter the tag (or prefix).",
      "Click 'Start Lookup'. You'll get a job ID and live progress.",
      "Leave the page if you like — jobs run server-side and resume here when you come back.",
      "When the job finishes, download the report. Each row shows whether the tag was present and lists the matching tags found.",
    ],
    templates: [],
  };

  const guideTips = [
    "Read-only: this tool only reads Shopify product tags, it never adds or removes anything.",
    "Why async: Shopify's API is rate-limited (~2 calls/sec). A 100k-row file can take many hours — the job tracks server-side so you can close the tab.",
    "Identifier priority in Auto mode: SKU (parent) → SHOPIFY_PRODUCT_ID → HANDLE. The first non-blank one is used; misses cascade to the next.",
    "Prefix mode is what you want for tag families like 'on_map_<date>' or 'offmap_<date>' — include the trailing separator (e.g. 'on_map_') so you don't accidentally match unrelated tags.",
    "The output workbook has a Summary sheet (totals) and a Results sheet listing per row: Has Tag (Yes/No), Matching Tags, All Tags, and the matched product.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prevStatus = previous[j.job_id];
      if (prevStatus && prevStatus !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(`Tag lookup ${j.job_id} complete. Report is ready.`, "success");
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `Tag lookup ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload an input file.", "error");
      return;
    }

    const trimmedTag = tag.trim();
    if (!trimmedTag) {
      addToast("Please enter the tag (or prefix) to look up.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("input_file", inputFile);
    formData.append("tag", trimmedTag);
    formData.append("match_mode", matchMode);
    if (identifierColumn) {
      formData.append("identifier_column", identifierColumn);
    }
    const trimmedSheet = sheetName.trim();
    if (trimmedSheet) {
      formData.append("sheet_name", trimmedSheet);
    }
    formData.append("include_archived", includeArchived ? "true" : "false");

    setUploading(true);
    setUploadProgress(0);

    const endpoint = ENDPOINT_FOR_LOOKUP[lookupMode] || ENDPOINT_FOR_LOOKUP[LOOKUP_IDENTIFIER];
    try {
      const response = await flaskApi.post(endpoint, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setUploadProgress(percentCompleted);
        },
      });

      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      addToast(`Tag lookup ${jobId} queued. Tracking progress…`, "info");
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to start tag lookup.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/shopify/lookup-product-tag/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "product_tag_lookup_report.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download report.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Tag Lookup"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input File</h3>
              </div>
              <ToolFileUploadField
                label="Product list"
                file={inputFile}
                setFile={setInputFile}
                accept=".csv,.xlsx,.xls"
                helperText={
                  identifierColumn
                    ? `CSV or XLSX with a ${ID_COL_LABEL[identifierColumn].replace(/ column$/, "")} column.`
                    : "CSV or XLSX with at least one of: SKU (parent SKU), SHOPIFY_PRODUCT_ID, HANDLE."
                }
              />
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Tab Name <span className="tool-section-optional">(optional)</span></h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Excel tab to read</label>
                <p className="tool-helper-text">
                  Only used for .xlsx / .xls files with multiple tabs. Leave blank to use the first tab. Case-sensitive — must match the tab name exactly.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="e.g. Outlet Store"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  disabled={uploading}
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Match Mode</h3>
              </div>
              <div className="tool-field-stack">
                <div className="tool-radio-group" role="radiogroup" aria-label="Match mode">
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="match-mode"
                      value={MATCH_EXACT}
                      checked={matchMode === MATCH_EXACT}
                      onChange={() => setMatchMode(MATCH_EXACT)}
                      disabled={uploading}
                    />
                    <span>Exact</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="match-mode"
                      value={MATCH_PREFIX}
                      checked={matchMode === MATCH_PREFIX}
                      onChange={() => setMatchMode(MATCH_PREFIX)}
                      disabled={uploading}
                    />
                    <span>Prefix</span>
                  </label>
                  <span className="tool-helper-text tool-radio-hint">
                    {matchHelperText(matchMode)}
                  </span>
                </div>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Lookup By</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="identifier-column">
                  Match Shopify products via
                </label>
                <select
                  id="identifier-column"
                  className="tool-input"
                  value={identifierColumn}
                  onChange={(event) => setIdentifierColumn(event.target.value)}
                  disabled={uploading}
                >
                  {ID_COL_OPTIONS.map((opt) => (
                    <option key={opt.value || "auto"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="tool-helper-text">
                  {ID_COL_HELPER[identifierColumn]}
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Archived Products</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-checkbox-option">
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(event) => setIncludeArchived(event.target.checked)}
                    disabled={uploading}
                  />
                  <span>Also look up archived products</span>
                </label>
                <span className="tool-helper-text">
                  By default only active products are checked. Tick this to also check archived products — matched archived products are flagged in the report. Draft products are always skipped.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Tag</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">
                  {matchMode === MATCH_PREFIX ? "Tag prefix to look up" : "Tag to look up"}
                </label>
                <p className="tool-helper-text">
                  {matchMode === MATCH_PREFIX
                    ? "Every tag on the matched product whose name starts with this value counts as a match. Include the trailing separator (e.g. 'on_map_') to avoid accidental matches."
                    : "The exact tag value to look for on each matched product (case-insensitive)."}
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder={matchMode === MATCH_PREFIX ? "e.g. on_map_" : "e.g. spring-2026"}
                  value={tag}
                  onChange={(event) => setTag(event.target.value)}
                  disabled={uploading}
                  required
                />
              </div>
            </section>

            <div className="tool-action-stack">
              <button
                type="submit"
                className="tool-btn-primary"
                disabled={uploading}
              >
                {uploading ? "Uploading…" : "Start Lookup"}
              </button>

              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Tag Lookups"
          emptyText="No tag lookups yet. Submit a file above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
                const progress = j.progress || {};
                const total = Number(progress.total) || 0;
                const processed = Number(progress.processed) || 0;
                const pct =
                  total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                const jobLookup = j.lookup_mode === LOOKUP_TITLE ? LOOKUP_TITLE : LOOKUP_IDENTIFIER;
                const jobMatch = j.match_mode === MATCH_PREFIX ? MATCH_PREFIX : MATCH_EXACT;
                return (
                  <div key={j.job_id} className="tool-job-status">
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Job ID</span>
                      <span className="tool-job-value">{j.job_id}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Lookup</span>
                      <span className="tool-job-value">
                        {j.identifier_column
                          ? ID_COL_LABEL[j.identifier_column] || j.identifier_column
                          : LOOKUP_LABEL[jobLookup]}
                      </span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Match</span>
                      <span className="tool-job-value">{MATCH_LABEL[jobMatch]}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Tag</span>
                      <span className="tool-job-value">{j.tag || "—"}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Input</span>
                      <span className="tool-job-value">{j.input_filename || "—"}</span>
                    </div>
                    {j.sheet_name ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Tab</span>
                        <span className="tool-job-value">{j.sheet_name}</span>
                      </div>
                    ) : null}
                    {j.include_archived ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Archived</span>
                        <span className="tool-job-value">Included</span>
                      </div>
                    ) : null}
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Status</span>
                      <span className={`tool-job-value tool-job-status-${j.status}`}>
                        {j.status}
                      </span>
                    </div>
                    {total > 0 ? (
                      <>
                        <div className="tool-job-status-row">
                          <span className="tool-job-label">Progress</span>
                          <span className="tool-job-value">
                            {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    ) : null}
                    <div className="tool-job-counts">
                      <span>Has tag: {progress.has_tag || 0}</span>
                      <span>No tag: {progress.no_tag || 0}</span>
                      <span>Not found: {progress.not_found || 0}</span>
                      <span>Errors: {progress.errors || 0}</span>
                      <span>Skipped: {progress.skipped || 0}</span>
                    </div>
                    {j.status === STATUS_FAILED && j.error ? (
                      <div className="tool-job-error">Error: {j.error}</div>
                    ) : null}
                    {j.status === STATUS_DONE ? (
                      <button
                        type="button"
                        className="tool-btn-primary"
                        onClick={() => handleDownload(j)}
                        disabled={downloadingJobId === j.job_id}
                      >
                        {downloadingJobId === j.job_id ? "Downloading…" : "Download Report"}
                      </button>
                    ) : null}
                  </div>
                );
              }}
            />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default TagLookupTool;
